import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import './ClienteDashboard.css';

interface ProjetoCliente {
  id: string;
  nome: string;
  endereco?: string;
  progressoGeral?: number;
  progresso?: number;
  dataInicio?: string;
  dataPrevistaConclusao?: string;
  criadoEm?: string;
  atualizadoEm?: string;
  totalFotos?: number;
  totalEtapas?: number;
  etapasCompletas?: number;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function tempoRelativo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHoras = Math.floor(diffMin / 60);
  const diffDias = Math.floor(diffHoras / 24);

  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin} min`;
  if (diffHoras < 24) return `há ${diffHoras}h`;
  if (diffDias < 7) return `há ${diffDias} dia${diffDias > 1 ? 's' : ''}`;
  return formatDate(dateStr);
}

export default function ClienteDashboard() {
  const [projetos, setProjetos] = useState<ProjetoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchProjetos() {
      try {
        setLoading(true);
        const data = await api.get('/empreendimentos');
        setProjetos(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar projetos');
      } finally {
        setLoading(false);
      }
    }
    fetchProjetos();
  }, []);

  if (loading) {
    return (
      <div className="cliente-dashboard">
        <h1 className="cliente-dashboard__title">Meus Projetos</h1>
        <p className="cliente-dashboard__loading">Carregando...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cliente-dashboard">
        <h1 className="cliente-dashboard__title">Meus Projetos</h1>
        <p className="cliente-dashboard__error">{error}</p>
      </div>
    );
  }

  if (projetos.length === 0) {
    return (
      <div className="cliente-dashboard">
        <h1 className="cliente-dashboard__title">Meus Projetos</h1>
        <div className="cliente-dashboard__empty">
          <span className="cliente-dashboard__empty-icon">🏗️</span>
          <p>Nenhum projeto vinculado à sua conta.</p>
          <p style={{ fontSize: '0.85rem', color: '#666' }}>
            Entre em contato com o responsável pela obra para solicitar acesso.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cliente-dashboard">
      <h1 className="cliente-dashboard__title">Meus Projetos</h1>
      <p className="cliente-dashboard__subtitle">
        Acompanhe o progresso das suas obras em tempo real
      </p>

      <div className="cliente-projetos-grid">
        {projetos.map((projeto) => {
          const progresso = projeto.progressoGeral ?? projeto.progresso ?? 0;
          const isRecente = projeto.atualizadoEm &&
            (new Date().getTime() - new Date(projeto.atualizadoEm).getTime()) < 24 * 60 * 60 * 1000;

          return (
            <Link
              key={projeto.id}
              to={`/empreendimentos/${projeto.id}`}
              className={`cliente-projeto-card ${isRecente ? 'cliente-projeto-card--novo' : ''}`}
            >
              {isRecente && (
                <span className="cliente-projeto-card__badge">🔔 Atualizado</span>
              )}

              <h3 className="cliente-projeto-card__nome">{projeto.nome}</h3>

              {projeto.endereco && (
                <p className="cliente-projeto-card__endereco">📍 {projeto.endereco}</p>
              )}

              {/* Barra de progresso */}
              <div className="cliente-projeto-card__progresso">
                <div className="cliente-projeto-card__progresso-label">
                  <span>Progresso geral</span>
                  <span className="cliente-projeto-card__progresso-valor">{progresso}%</span>
                </div>
                <div className="cliente-projeto-card__progresso-track">
                  <div
                    className="cliente-projeto-card__progresso-fill"
                    style={{ width: `${progresso}%` }}
                  />
                </div>
              </div>

              {/* Info grid */}
              <div className="cliente-projeto-card__info">
                {projeto.dataInicio && (
                  <div className="cliente-projeto-card__info-item">
                    <span className="cliente-projeto-card__info-label">Início</span>
                    <span className="cliente-projeto-card__info-value">{formatDate(projeto.dataInicio)}</span>
                  </div>
                )}
                {projeto.dataPrevistaConclusao && (
                  <div className="cliente-projeto-card__info-item">
                    <span className="cliente-projeto-card__info-label">Previsão</span>
                    <span className="cliente-projeto-card__info-value">{formatDate(projeto.dataPrevistaConclusao)}</span>
                  </div>
                )}
                {projeto.totalFotos !== undefined && (
                  <div className="cliente-projeto-card__info-item">
                    <span className="cliente-projeto-card__info-label">📷 Fotos</span>
                    <span className="cliente-projeto-card__info-value">{projeto.totalFotos}</span>
                  </div>
                )}
                {projeto.atualizadoEm && (
                  <div className="cliente-projeto-card__info-item">
                    <span className="cliente-projeto-card__info-label">Última atualização</span>
                    <span className="cliente-projeto-card__info-value">{tempoRelativo(projeto.atualizadoEm)}</span>
                  </div>
                )}
              </div>

              <span className="cliente-projeto-card__ver-mais">Ver detalhes →</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
